import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, message, Tabs, Table, Modal, Input, InputNumber, Select, Tag, Space, Popconfirm, Empty,
} from 'antd';
import { PlusOutlined, ReloadOutlined, InsuranceOutlined, TeamOutlined, PayCircleOutlined, FileTextOutlined, MoneyCollectOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch, notifyDataChanged } from '../utils/api.js';

const INK = '#26201C';
const CORAL = '#D85A30';
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const fmtYuan = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '¥0';
  const neg = n < 0;
  const abs = Math.abs(n);
  const s = abs >= 10000 ? (abs / 10000).toFixed(2) + '万' : abs.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
  return (neg ? '-¥' : '¥') + s;
};

// 最近 12 个月 + 未来 6 个月
function monthOptions() {
  const out = [];
  const now = dayjs().startOf('month');
  for (let i = 12; i >= -6; i--) {
    const m = now.subtract(i, 'month').format('YYYY-MM');
    out.push({ value: m, label: m });
  }
  return out;
}

export default function SocialInsurance() {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [accounts, setAccounts] = useState([]); // 资金账户（付款账户）

  // 每月缴费
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [calc, setCalc] = useState(null); // { list, unitTotal, personalTotal, paidCount }
  const [payAccount, setPayAccount] = useState('');
  const [records, setRecords] = useState([]);

  // 工资台账
  const [payrollMonth, setPayrollMonth] = useState(dayjs().format('YYYY-MM'));
  const [payrollList, setPayrollList] = useState([]);
  const [payrollMeta, setPayrollMeta] = useState({ employees: [], accounts: [] });
  const [openPayroll, setOpenPayroll] = useState(false);
  const [pay, setPay] = useState({ employee_id: '', name: '', gross: '', personal_si: '', other_deduct: '0', account_id: '', pay_date: dayjs().format('YYYY-MM-DD'), remark: '' });

  // 员工弹窗
  const [openEmp, setOpenEmp] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [emp, setEmp] = useState({ name: '', city: '', base: '', unit_rate: '', personal_rate: '', start_month: '', status: '在职', remark: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, a] = await Promise.all([
        apiFetch('/api/social/employee').then(x => x.json()),
        apiFetch('/api/cash-account').then(x => x.json()),
      ]);
      if (e.success) setEmployees(e.data || []);
      else message.error(e.error || '员工档案加载失败');
      if (a.success) setAccounts((a.accounts || []).filter(x => x.kind !== 'shop'));
    } catch (err) { message.error('社保加载失败：' + (err.message || '')); }
    setLoading(false);
  }, []);

  const loadCalc = useCallback(async (m) => {
    try {
      const r = await apiFetch('/api/social/calc?month=' + m).then(x => x.json());
      if (r.success) setCalc(r.data || null);
      else message.error(r.error || '计算失败');
    } catch (e) { message.error('计算失败：' + e.message); }
    try {
      const rec = await apiFetch('/api/social/records?month=' + m).then(x => x.json());
      if (rec.success) setRecords(rec.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadPayroll = useCallback(async (m) => {
    try {
      const [meta, list] = await Promise.all([
        apiFetch('/api/payroll/meta').then(x => x.json()),
        apiFetch('/api/payroll?month=' + m).then(x => x.json()),
      ]);
      if (meta.success) setPayrollMeta(meta);
      if (list.success) setPayrollList(list.data || []);
    } catch (e) { message.error('工资加载失败：' + (e.message || '')); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCalc(month); }, [month, loadCalc]);
  useEffect(() => { loadPayroll(payrollMonth); }, [payrollMonth, loadPayroll]);

  const afterMutate = (msg) => { message.success(msg); notifyDataChanged(); load(); loadCalc(month); };

  // 员工保存
  const submitEmp = async () => {
    if (!emp.name) return message.warning('请填写员工姓名');
    if (!emp.base || Number(emp.base) <= 0) return message.warning('请填写正数的缴费基数');
    if (emp.start_month && !/^\d{4}-\d{2}$/.test(emp.start_month)) return message.warning('起缴月份格式应为 YYYY-MM');
    try {
      const body = {
        name: emp.name, city: emp.city, base: Number(emp.base),
        unit_rate: emp.unit_rate === '' ? null : Number(emp.unit_rate),
        personal_rate: emp.personal_rate === '' ? null : Number(emp.personal_rate),
        start_month: emp.start_month, status: emp.status, remark: emp.remark,
      };
      if (editEmp) body.id = editEmp.id;
      const res = await apiFetch('/api/social/employee', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(x => x.json());
      if (res.success) { setOpenEmp(false); setEditEmp(null); afterMutate(editEmp ? '员工档案已保存' : '员工已新增'); }
      else message.error(res.error || '保存失败');
    } catch (e) { message.error('保存失败：' + e.message); }
  };
  const delEmp = async (id) => {
    try {
      const res = await apiFetch('/api/social/employee/' + id, { method: 'DELETE' }).then(x => x.json());
      if (res.success) afterMutate('已删除');
      else message.error(res.error || '删除失败');
    } catch (e) { message.error('删除失败：' + e.message); }
  };

  // 确认缴费
  const doPay = async () => {
    if (!payAccount) return message.warning('请选择付款账户');
    const pending = (calc && calc.list || []).filter(x => !x.paid);
    if (pending.length === 0) return message.info('该月没有待缴员工');
    try {
      const res = await apiFetch('/api/social/pay', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, account_id: payAccount }),
      }).then(x => x.json());
      if (res.success) { afterMutate('已缴纳 ' + res.paid + ' 人社保（单位合计 ' + fmtYuan(res.unitTotal) + '）'); }
      else message.error(res.error || '缴费失败');
    } catch (e) { message.error('缴费失败：' + e.message); }
  };

  // 工资：选择员工自动带出姓名+个人社保代扣
  const pickEmployee = (empId) => {
    const e = payrollMeta.employees.find(x => String(x.id) === String(empId));
    setPay(p => ({ ...p, employee_id: empId, name: e ? e.name : '', personal_si: e ? e.personal_si : p.personal_si }));
  };
  const submitPayroll = async () => {
    if (!pay.name) return message.warning('请填写员工姓名');
    if (!pay.gross || Number(pay.gross) <= 0) return message.warning('请填写正数的应发工资');
    try {
      const res = await apiFetch('/api/payroll', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: pay.employee_id || null, name: pay.name, month: payrollMonth,
          gross: Number(pay.gross), personal_si: Number(pay.personal_si || 0), other_deduct: Number(pay.other_deduct || 0),
          account_id: pay.account_id, pay_date: pay.pay_date, remark: pay.remark,
        }),
      }).then(x => x.json());
      if (res.success) { setOpenPayroll(false); message.success('工资已发放（实发 ' + fmtYuan(res.net) + '）'); notifyDataChanged(); loadPayroll(payrollMonth); }
      else message.error(res.error || '发放失败');
    } catch (e) { message.error('发放失败：' + e.message); }
  };
  const delPayroll = async (id) => {
    try {
      const res = await apiFetch('/api/payroll/' + id, { method: 'DELETE' }).then(x => x.json());
      if (res.success) { message.success('已删除'); notifyDataChanged(); loadPayroll(payrollMonth); }
      else message.error(res.error || '删除失败');
    } catch (e) { message.error('删除失败：' + e.message); }
  };

  // ===== 员工档案 Tab =====
  const empColumns = [
    { title: '姓名', dataIndex: 'name', width: 100, render: v => <b>{v}</b> },
    { title: '参保地', dataIndex: 'city', width: 120, render: v => v || '—' },
    { title: '缴费基数', dataIndex: 'base', width: 110, align: 'right', render: v => fmtYuan(v) },
    { title: '单位比例', dataIndex: 'unit_rate', width: 90, align: 'right', render: v => <span>{v}%</span> },
    { title: '个人比例', dataIndex: 'personal_rate', width: 90, align: 'right', render: v => <span>{v}%</span> },
    { title: '单位应缴/月', dataIndex: 'unit_amount', width: 110, align: 'right', render: v => <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</b> },
    { title: '个人应缴/月', dataIndex: 'personal_amount', width: 110, align: 'right', render: v => <span style={{ color: '#A08C82' }}>{fmtYuan(v)}</span> },
    { title: '起缴月', dataIndex: 'start_month', width: 90, render: v => v || '—' },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: s => s === '停保' ? <Tag color="default" style={{ borderRadius: 999 }}>停保</Tag> : <Tag color="success" style={{ borderRadius: 999 }}>在职</Tag>,
    },
    {
      title: '操作', width: 130, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => { setEditEmp(r); setEmp({ name: r.name, city: r.city, base: r.base, unit_rate: r.unit_rate, personal_rate: r.personal_rate, start_month: r.start_month || '', status: r.status, remark: r.remark || '' }); setOpenEmp(true); }}>编辑</Button>
          <Popconfirm title="删除该员工？" onConfirm={() => delEmp(r.id)}><Button size="small" danger>删</Button></Popconfirm>
        </Space>
      ),
    },
  ];
  const empTab = (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditEmp(null); setEmp({ name: '', city: '', base: '', unit_rate: '', personal_rate: '', start_month: '', status: '在职', remark: '' }); setOpenEmp(true); }}>新增员工</Button>
        <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
      </Space>
      <div style={{ fontSize: 12, color: '#A08C82', marginBottom: 8, lineHeight: 1.7 }}>
        单位比例 / 个人比例默认按全国常见标准（25% / 10.5%），可针对每个员工单独改。单位应缴每月自动 = 基数 × 单位比例，进经营费用；个人应缴仅存档展示（从工资扣，系统暂不记）。
      </div>
      <Table rowKey="id" size="small" dataSource={employees} columns={empColumns} pagination={false}
        locale={{ emptyText: <Empty description="还没有员工社保档案，点「新增员工」登记" /> }} />
    </div>
  );

  // ===== 每月缴费 Tab =====
  const payColumns = [
    { title: '姓名', dataIndex: 'name', width: 100 },
    { title: '基数', dataIndex: 'base', width: 110, align: 'right', render: v => fmtYuan(v) },
    { title: '单位应缴', dataIndex: 'unit_amount', width: 120, align: 'right', render: v => <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</b> },
    { title: '个人应缴', dataIndex: 'personal_amount', width: 110, align: 'right', render: v => <span style={{ color: '#A08C82' }}>{fmtYuan(v)}</span> },
    {
      title: '状态', dataIndex: 'paid', width: 90,
      render: p => p ? <Tag color="success" style={{ borderRadius: 999 }}>已缴</Tag> : <Tag color="warning" style={{ borderRadius: 999 }}>待缴</Tag>,
    },
  ];
  const payTab = (
    <div>
      <Card size="small" title="每月社保缴费" style={{ borderRadius: 14, border: '1px solid #F3E2D8', marginBottom: 12 }} styles={{ body: { padding: 16 } }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: '#5F5E5A' }}>缴费月份</span>
          <Select style={{ width: 140 }} value={month} onChange={setMonth} options={monthOptions()} />
          <span style={{ fontSize: 13, color: '#5F5E5A', marginLeft: 8 }}>付款账户</span>
          <Select style={{ width: 160 }} placeholder="选择付款账户" value={payAccount || undefined} onChange={setPayAccount}
            options={accounts.map(a => ({ value: String(a.id), label: a.name }))} />
          <Button type="primary" icon={<PayCircleOutlined />} onClick={doPay} style={{ background: CORAL }}>
            确认缴纳（单位合计 {fmtYuan(calc ? calc.unitTotal : 0)}）
          </Button>
        </Space>
        <Table rowKey="id" size="small" dataSource={(calc && calc.list) || []} columns={payColumns} pagination={false}
          locale={{ emptyText: '该月没有需要缴纳的员工（未建档或起缴月未到）' }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><b>合计</b></Table.Summary.Cell>
              <Table.Summary.Cell index={1} />
              <Table.Summary.Cell index={2} align="right"><b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(calc ? calc.unitTotal : 0)}</b></Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right"><span style={{ color: '#A08C82' }}>{fmtYuan(calc ? calc.personalTotal : 0)}</span></Table.Summary.Cell>
              <Table.Summary.Cell index={4} />
            </Table.Summary.Row>
          )}
        />
      </Card>

      <Card size="small" title={'本月已缴记录（' + month + '）'} style={{ borderRadius: 14, border: '1px solid #F3E2D8' }} styles={{ body: { padding: 16 } }}>
        <Table rowKey="id" size="small" dataSource={records} pagination={false}
          locale={{ emptyText: '本月还没有缴费记录' }}
          columns={[
            { title: '姓名', dataIndex: 'employee_name', width: 120 },
            { title: '单位缴纳', dataIndex: 'unit_amount', width: 120, align: 'right', render: v => <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</b> },
            { title: '个人部分', dataIndex: 'personal_amount', width: 110, align: 'right', render: v => <span style={{ color: '#A08C82' }}>{fmtYuan(v)}</span> },
            { title: '付款账户', dataIndex: 'account_id', width: 120, render: v => { const a = accounts.find(x => String(x.id) === String(v)); return a ? a.name : v; } },
            { title: '记账日期', dataIndex: 'biz_date', width: 110, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
          ]} />
      </Card>
    </div>
  );

  // ===== 工资台账 Tab =====
  const payrollColumns = [
    { title: '姓名', dataIndex: 'name', width: 100, render: v => <b>{v}</b> },
    { title: '月份', dataIndex: 'month', width: 90, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { title: '应发工资', dataIndex: 'gross', width: 120, align: 'right', render: v => <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</b> },
    { title: '个人社保代扣', dataIndex: 'personal_si', width: 120, align: 'right', render: v => <span style={{ color: '#A08C82' }}>{fmtYuan(v)}</span> },
    { title: '其他扣款', dataIndex: 'other_deduct', width: 100, align: 'right', render: v => <span style={{ color: '#A08C82' }}>{fmtYuan(v)}</span> },
    { title: '实发', dataIndex: 'net', width: 110, align: 'right', render: v => <b style={{ color: '#3F8600', fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</b> },
    { title: '付款账户', dataIndex: 'account_id', width: 110, render: v => { const a = (payrollMeta.accounts || []).find(x => String(x.id) === String(v)); return a ? a.name : (v || '—'); } },
    { title: '发放日期', dataIndex: 'pay_date', width: 110, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    {
      title: '操作', width: 80, align: 'center',
      render: (_, r) => <Popconfirm title="删除该条工资？会同步删除对应的人工支出流水" onConfirm={() => delPayroll(r.id)}><Button size="small" danger>删</Button></Popconfirm>,
    },
  ];
  const payrollTab = (
    <div>
      <Card size="small" title={'工资发放（' + payrollMonth + '）'} style={{ borderRadius: 14, border: '1px solid #F3E2D8', marginBottom: 12 }} styles={{ body: { padding: 16 } }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: '#5F5E5A' }}>月份</span>
          <Select style={{ width: 140 }} value={payrollMonth} onChange={setPayrollMonth} options={monthOptions()} />
          <Button type="primary" icon={<MoneyCollectOutlined />} style={{ background: CORAL }}
            onClick={() => { setPay({ employee_id: '', name: '', gross: '', personal_si: '', other_deduct: '0', account_id: '', pay_date: dayjs().format('YYYY-MM-DD'), remark: '' }); setOpenPayroll(true); }}>发工资</Button>
          <span style={{ fontSize: 13, color: '#5F5E5A' }}>
            应发合计 <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(payrollList.reduce((s, p) => s + Number(p.gross || 0), 0))}</b>
            ｜ 实发合计 <b style={{ color: '#3F8600', fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(payrollList.reduce((s, p) => s + Number(p.net || 0), 0))}</b>
          </span>
        </Space>
        <Table rowKey="id" size="small" dataSource={payrollList} columns={payrollColumns} pagination={false}
          locale={{ emptyText: '该月还没有工资发放记录，点「发工资」' }} />
        <div style={{ fontSize: 12, color: '#A08C82', marginTop: 10, lineHeight: 1.7 }}>
          应发工资进「人工」经营费用（减净利）；个人社保代扣从应发里扣（员工承担）；单位社保已在「每月缴费」单独记。发工资会自动生成一条 6007 人工支出流水并扣对应账户余额。
        </div>
      </Card>
    </div>
  );

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: INK }}>
            <InsuranceOutlined style={{ color: CORAL, marginRight: 8 }} />员工社保
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', marginTop: 4 }}>
            员工社保档案 + 每月缴费　|　缴费只记单位部分（企业成本），进经营费用、扣账户余额，与账务流水/公司看板打通
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
      </div>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'employees', label: <span><TeamOutlined /> 员工档案</span>, children: tab === 'employees' ? empTab : null },
          { key: 'pay', label: <span><PayCircleOutlined /> 每月缴费</span>, children: tab === 'pay' ? payTab : null },
          { key: 'payroll', label: <span><MoneyCollectOutlined /> 工资</span>, children: tab === 'payroll' ? payrollTab : null },
        ]}
      />

      {/* 员工档案弹窗 */}
      <Modal title={editEmp ? '编辑员工社保档案' : '新增员工社保档案'} open={openEmp} onCancel={() => { setOpenEmp(false); setEditEmp(null); }} onOk={submitEmp} okText="保存" cancelText="取消" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>姓名 <span style={{ color: '#CF1322' }}>*</span></div>
            <Input value={emp.name} onChange={e => setEmp({ ...emp, name: e.target.value })} placeholder="员工姓名" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>参保地</div>
            <Input value={emp.city} onChange={e => setEmp({ ...emp, city: e.target.value })} placeholder="如：江苏淮安（可选）" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>缴费基数（元/月）<span style={{ color: '#CF1322' }}>*</span></div>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} value={emp.base || null} onChange={v => setEmp({ ...emp, base: v })} placeholder="如 4500" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>单位比例 %</div>
              <InputNumber style={{ width: '100%' }} min={0} precision={1} value={emp.unit_rate === '' ? null : emp.unit_rate} onChange={v => setEmp({ ...emp, unit_rate: v == null ? '' : v })} placeholder="默认 25" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>个人比例 %</div>
              <InputNumber style={{ width: '100%' }} min={0} precision={1} value={emp.personal_rate === '' ? null : emp.personal_rate} onChange={v => setEmp({ ...emp, personal_rate: v == null ? '' : v })} placeholder="默认 10.5" />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>起缴月份</div>
            <Input value={emp.start_month} onChange={e => setEmp({ ...emp, start_month: e.target.value })} placeholder="YYYY-MM（如 2026-10，留空则立即起缴）" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>状态</div>
            <Select style={{ width: '100%' }} value={emp.status} onChange={v => setEmp({ ...emp, status: v })}
              options={[{ value: '在职', label: '在职' }, { value: '停保', label: '停保' }]} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>备注</div>
            <Input value={emp.remark} onChange={e => setEmp({ ...emp, remark: e.target.value })} placeholder="可选" />
          </div>
        </Space>
      </Modal>

      {/* 工资发放弹窗 */}
      <Modal title={'发放工资（' + payrollMonth + '）'} open={openPayroll} onCancel={() => setOpenPayroll(false)} onOk={submitPayroll} okText="发放" cancelText="取消" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>选择员工（自动带出姓名和个人社保代扣）</div>
            <Select style={{ width: '100%' }} value={pay.employee_id || undefined} onChange={pickEmployee} placeholder="从社保档案选择员工（可选，也可手填）" allowClear
              options={(payrollMeta.employees || []).map(e => ({ value: e.id, label: e.name + '（个人社保代扣 ' + fmtYuan(e.personal_si) + '）' }))} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>姓名 <span style={{ color: '#CF1322' }}>*</span></div>
            <Input value={pay.name} onChange={e => setPay({ ...pay, name: e.target.value })} placeholder="员工姓名" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>应发工资（元）<span style={{ color: '#CF1322' }}>*</span></div>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} value={pay.gross || null} onChange={v => setPay({ ...pay, gross: v })} placeholder="如 8000" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>个人社保代扣（元）</div>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} value={pay.personal_si === '' ? null : pay.personal_si} onChange={v => setPay({ ...pay, personal_si: v == null ? '' : v })} placeholder="自动带出，可改" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>其他扣款（元）</div>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} value={pay.other_deduct === '' ? null : pay.other_deduct} onChange={v => setPay({ ...pay, other_deduct: v == null ? '' : v })} placeholder="默认 0" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>付款账户</div>
              <Select style={{ width: '100%' }} value={pay.account_id || undefined} onChange={v => setPay({ ...pay, account_id: v })} placeholder="选择付款账户"
                options={(payrollMeta.accounts || []).map(a => ({ value: String(a.id), label: a.name }))} />
            </div>
          </div>
          <div style={{ background: '#FFF8F4', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: INK }}>
            实发工资 = 应发 <b>{fmtYuan(Number(pay.gross) || 0)}</b> − 个人社保 <b>{fmtYuan(Number(pay.personal_si) || 0)}</b> − 其他扣款 <b>{fmtYuan(Number(pay.other_deduct) || 0)}</b> = <b style={{ color: '#3F8600' }}>{fmtYuan((Number(pay.gross) || 0) - (Number(pay.personal_si) || 0) - (Number(pay.other_deduct) || 0))}</b>
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>发放日期</div>
            <Input value={pay.pay_date} onChange={e => setPay({ ...pay, pay_date: e.target.value })} placeholder="YYYY-MM-DD" />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
